import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Button,
  Card,
  CardBody,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import {
  ExternalReceiverProfile,
  getReceiverShipmentsPage,
  ReceiverShipmentRow,
} from '../../../../common/src/api/external-profiles';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import {
  Alert,
  EmptyState,
  KpiCard,
  LoadingState,
  PageHeader,
  PageLayout,
  StatusChip,
} from '../../../../common/src/components/ui';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';

interface ReceiverShipmentsPageProps {
  profile: ExternalReceiverProfile;
  view: 'summary' | 'detail';
}

interface GroupedShipment {
  id: number;
  exporter_name: string;
  client_name: string;
  reference_number?: string | null;
  container_number?: string | null;
  ship_name?: string | null;
  ship_etd?: string | null;
  ship_eta?: string | null;
  destination?: string | null;
  status: 'pending' | 'settled';
  total_quantity: number;
  total_kilos: number;
}

const getLocale = (language?: string) => {
  if (language?.startsWith('es')) return 'es-CL';
  if (language?.startsWith('en')) return 'en-US';
  return 'zh-CN';
};

const formatNumber = (
  value: number,
  locale: string,
  maximumFractionDigits = 0
) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits }).format(
    Number(value) || 0
  );

const formatDate = (value: string | null | undefined, locale: string) => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const toExportDate = (value: string | null | undefined) => value || '';

const getWeekLabel = (value: string | null | undefined) => {
  if (!value) return 'Sin ETA';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  const start = new Date(date.getFullYear(), 0, 1);
  const dayOfYear = Math.floor(
    (date.getTime() - start.getTime()) / 86400000 + 1
  );
  return `${date.getFullYear()} W${Math.ceil(dayOfYear / 7)}`;
};

const sumByKey = <T,>(
  rows: T[],
  getKey: (row: T) => string,
  getValue: (row: T) => number
) => {
  const map = new Map<string, number>();
  rows.forEach((row) => {
    const key = getKey(row) || '-';
    map.set(key, (map.get(key) || 0) + getValue(row));
  });
  return Array.from(map, ([label, value]) => ({ label, value })).sort(
    (a, b) => b.value - a.value
  );
};

const MiniBarList = ({
  rows,
  locale,
  unit,
  emptyLabel,
}: {
  rows: Array<{ label: string; value: number }>;
  locale: string;
  unit: string;
  emptyLabel: string;
}) => {
  const maxValue = Math.max(1, ...rows.map((row) => row.value));

  return (
    <div className='space-y-3'>
      {rows.length === 0 ? (
        <EmptyState title={emptyLabel} />
      ) : (
        rows.map((row) => (
          <div key={row.label} className='space-y-1'>
            <div className='flex items-center justify-between gap-3'>
              <span className='text-sm text-default-700 truncate'>
                {row.label}
              </span>
              <span className='text-sm font-medium text-foreground'>
                {formatNumber(row.value, locale)} {unit}
              </span>
            </div>
            <div className='h-2 rounded-full bg-default-100 overflow-hidden'>
              <div
                className='h-full rounded-full bg-brand-700'
                style={{ width: `${Math.max((row.value / maxValue) * 100, 3)}%` }}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const ReceiverShipmentsPage = ({
  profile,
  view,
}: ReceiverShipmentsPageProps) => {
  const { t, currentLanguage } = useTranslation('receiver');
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const locale = useMemo(() => getLocale(currentLanguage), [currentLanguage]);
  const [rows, setRows] = useState<ReceiverShipmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [selectedExporter, setSelectedExporter] = useState('all');
  const [selectedClient, setSelectedClient] = useState('all');

  const scopedRelationships = useMemo(() => {
    if (!selectedSeason || !selectedSpecies?.id) return [];
    return profile.relationships.filter((relationship) =>
      (relationship.scopes || []).some(
        (scope) =>
          scope.season === selectedSeason &&
          scope.species_id === selectedSpecies.id
      )
    );
  }, [profile.relationships, selectedSeason, selectedSpecies?.id]);

  const exporters = useMemo(() => {
    const map = new Map<number, string>();
    scopedRelationships.forEach((relationship) => {
      map.set(relationship.exporter_id, relationship.exporter_name);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [scopedRelationships]);

  const clients = useMemo(() => {
    return scopedRelationships.filter(
      (relationship) =>
        selectedExporter === 'all' ||
        relationship.exporter_id === Number(selectedExporter)
    );
  }, [scopedRelationships, selectedExporter]);

  useEffect(() => {
    if (
      selectedExporter !== 'all' &&
      !exporters.some((exporter) => exporter.id === Number(selectedExporter))
    ) {
      setSelectedExporter('all');
    }
  }, [exporters, selectedExporter]);

  useEffect(() => {
    if (
      selectedClient !== 'all' &&
      !clients.some(
        (client) => client.exporter_receiver_id === Number(selectedClient)
      )
    ) {
      setSelectedClient('all');
    }
  }, [clients, selectedClient]);

  useEffect(() => {
    const load = async () => {
      if (!selectedSeason || !selectedSpecies?.id) {
        setRows([]);
        setErrorKey(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorKey(null);
      try {
        const payload = await getReceiverShipmentsPage({
          season: selectedSeason,
          speciesId: selectedSpecies.id,
          exporterId:
            selectedExporter === 'all' ? null : Number(selectedExporter),
          exporterReceiverIds:
            selectedClient === 'all' ? null : [Number(selectedClient)],
        });
        setRows(payload.rows);
      } catch (loadError) {
        console.error('Error loading receiver shipments:', loadError);
        setErrorKey('shipments.loadError');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedSeason, selectedSpecies?.id, selectedExporter, selectedClient]);

  const groupedShipments = useMemo(() => {
    const grouped = new Map<number, GroupedShipment>();

    rows.forEach((row) => {
      const current = grouped.get(row.shipment_id);
      const quantity = Number(row.quantity) || 0;
      const kilos = quantity * (Number(row.box_weight) || 0);

      if (!current) {
        grouped.set(row.shipment_id, {
          id: row.shipment_id,
          exporter_name: row.exporter_name,
          client_name: row.client_name,
          reference_number: row.reference_number,
          container_number: row.container_number,
          ship_name: row.ship_name,
          ship_etd: row.ship_etd,
          ship_eta: row.ship_eta,
          destination:
            row.ship_destination_port || row.ship_destination_country || null,
          status: row.status || 'pending',
          total_quantity: quantity,
          total_kilos: kilos,
        });
        return;
      }

      current.total_quantity += quantity;
      current.total_kilos += kilos;
      if (current.status !== 'settled') {
        current.status = row.status || 'pending';
      }
    });

    return Array.from(grouped.values());
  }, [rows]);

  const summary = useMemo(() => {
    const containers = new Set(
      groupedShipments
        .map((shipment) => shipment.container_number)
        .filter(Boolean)
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const next14 = new Date(today);
    next14.setDate(today.getDate() + 14);
    const arrivingSoon = groupedShipments.filter((shipment) => {
      if (!shipment.ship_eta) return false;
      const eta = new Date(`${shipment.ship_eta}T00:00:00`);
      return eta >= today && eta <= next14;
    });

    return {
      totalShipments: groupedShipments.length,
      totalContainers: containers.size,
      totalBoxes: groupedShipments.reduce(
        (sum, shipment) => sum + shipment.total_quantity,
        0
      ),
      totalKg: groupedShipments.reduce(
        (sum, shipment) => sum + shipment.total_kilos,
        0
      ),
      pending: groupedShipments.filter(
        (shipment) => shipment.status === 'pending'
      ).length,
      settled: groupedShipments.filter(
        (shipment) => shipment.status === 'settled'
      ).length,
      arrivingSoon: arrivingSoon.length,
      arrivingSoonKg: arrivingSoon.reduce(
        (sum, shipment) => sum + shipment.total_kilos,
        0
      ),
    };
  }, [groupedShipments]);

  const topExporters = useMemo(
    () =>
      sumByKey(
        groupedShipments,
        (shipment) => shipment.exporter_name,
        (shipment) => shipment.total_kilos
      ).slice(0, 6),
    [groupedShipments]
  );

  const topClients = useMemo(
    () =>
      sumByKey(
        groupedShipments,
        (shipment) => shipment.client_name,
        (shipment) => shipment.total_kilos
      ).slice(0, 6),
    [groupedShipments]
  );

  const etaWeeks = useMemo(
    () =>
      sumByKey(
        groupedShipments,
        (shipment) => getWeekLabel(shipment.ship_eta),
        (shipment) => shipment.total_kilos
      ).sort((a, b) => a.label.localeCompare(b.label)),
    [groupedShipments]
  );

  const upcomingArrivals = useMemo(() => {
    return [...groupedShipments]
      .filter((shipment) => shipment.ship_eta)
      .sort((a, b) => String(a.ship_eta).localeCompare(String(b.ship_eta)))
      .slice(0, 8);
  }, [groupedShipments]);

  const handleExport = () => {
    const columns = [
      t('shared.exporter'),
      t('shared.client'),
      t('shared.reference'),
      t('shared.container'),
      t('shared.ship'),
      'ETD',
      'ETA',
      t('shipments.summary.destination'),
      t('shared.boxes'),
      t('shared.kg'),
      t('shared.status'),
    ];
    const exportRows = groupedShipments.map((shipment) => [
      shipment.exporter_name,
      shipment.client_name,
      shipment.reference_number || '',
      shipment.container_number || '',
      shipment.ship_name || '',
      toExportDate(shipment.ship_etd),
      toExportDate(shipment.ship_eta),
      shipment.destination || '',
      shipment.total_quantity,
      shipment.total_kilos,
      shipment.status === 'settled'
        ? t('shared.settled')
        : t('shared.pending'),
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([columns, ...exportRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shipments');
    XLSX.writeFile(workbook, `shipments-${selectedSeason || 'all'}.xlsx`);
  };

  const kpiCards = [
    {
      label: t('shipments.summary.totalShipments'),
      value: formatNumber(summary.totalShipments, locale),
      detail: t('shipments.summary.containersDetail', {
        count: formatNumber(summary.totalContainers, locale),
      }),
    },
    {
      label: t('shipments.summary.totalBoxes'),
      value: formatNumber(summary.totalBoxes, locale),
      detail: `${formatNumber(summary.totalKg, locale)} ${t('shared.kg')}`,
    },
    {
      label: t('shipments.summary.pending'),
      value: formatNumber(summary.pending, locale),
      detail: t('shipments.summary.settledDetail', {
        count: formatNumber(summary.settled, locale),
      }),
    },
    {
      label: t('shipments.summary.arrivingSoon'),
      value: formatNumber(summary.arrivingSoon, locale),
      detail: `${formatNumber(summary.arrivingSoonKg, locale)} ${t('shared.kg')}`,
    },
  ];

  return (
    <PageLayout className='h-full overflow-auto'>
      <PageHeader
        title={view === 'summary'
            ? t('shipments.summary.title')
            : t('shipments.detail.title')}
        description={view === 'summary'
            ? t('shipments.summary.subtitle')
            : t('shipments.subtitle')}
      />

      <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5'>
        <div className='flex flex-col md:flex-row gap-3'>
          <Select
            label={t('shared.exporter')}
            className='max-w-xs'
            selectedKeys={[selectedExporter]}
            onChange={(event) =>
              setSelectedExporter(event.target.value || 'all')
            }
          >
            <SelectItem key='all'>{t('shared.allExporters')}</SelectItem>
            {exporters.map((exporter) => (
              <SelectItem key={String(exporter.id)}>{exporter.name}</SelectItem>
            ))}
          </Select>
          <Select
            label={t('shared.client')}
            className='max-w-xs'
            selectedKeys={[selectedClient]}
            onChange={(event) => setSelectedClient(event.target.value || 'all')}
          >
            <SelectItem key='all'>{t('shared.allClients')}</SelectItem>
            {clients.map((client) => (
              <SelectItem key={String(client.exporter_receiver_id)}>
                {client.client_name}
              </SelectItem>
            ))}
          </Select>
        </div>
        {view === 'detail' ? (
          <Button
            variant='flat'
            onPress={handleExport}
            isDisabled={loading}
          >
            {t('shared.exportExcel')}
          </Button>
        ) : null}
      </div>

      {loading ? (
        <LoadingState />
      ) : errorKey ? (
        <Alert variant='danger' showIcon>
          {t(errorKey)}
        </Alert>
      ) : view === 'summary' ? (
        <div className='space-y-5'>
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
            {kpiCards.map((card) => (
              <KpiCard
                key={card.label}
                label={card.label}
                value={card.value}
                detail={card.detail}
              />
            ))}
          </div>

          <div className='grid grid-cols-1 xl:grid-cols-3 gap-5'>
            <Card shadow='sm'>
              <CardBody className='p-6'>
                <h2 className='text-base font-semibold text-foreground mb-4'>
                  {t('shipments.summary.byExporter')}
                </h2>
                <MiniBarList
                  rows={topExporters}
                  locale={locale}
                  unit='kg'
                  emptyLabel={t('shipments.empty')}
                />
              </CardBody>
            </Card>
            <Card shadow='sm'>
              <CardBody className='p-6'>
                <h2 className='text-base font-semibold text-foreground mb-4'>
                  {t('shipments.summary.byClient')}
                </h2>
                <MiniBarList
                  rows={topClients}
                  locale={locale}
                  unit='kg'
                  emptyLabel={t('shipments.empty')}
                />
              </CardBody>
            </Card>
            <Card shadow='sm'>
              <CardBody className='p-6'>
                <h2 className='text-base font-semibold text-foreground mb-4'>
                  {t('shipments.summary.byEtaWeek')}
                </h2>
                <MiniBarList
                  rows={etaWeeks}
                  locale={locale}
                  unit='kg'
                  emptyLabel={t('shipments.empty')}
                />
              </CardBody>
            </Card>
          </div>

          <Card shadow='sm'>
            <CardBody className='p-6'>
              <h2 className='text-base font-semibold text-foreground mb-4'>
                {t('shipments.summary.upcomingArrivals')}
              </h2>
              <Table
                removeWrapper
                aria-label={t('shipments.summary.upcomingArrivals')}
              >
                <TableHeader>
                  <TableColumn>{t('shared.eta')}</TableColumn>
                  <TableColumn>{t('shared.exporter')}</TableColumn>
                  <TableColumn>{t('shared.reference')}</TableColumn>
                  <TableColumn>{t('shared.kg')}</TableColumn>
                  <TableColumn>{t('shared.status')}</TableColumn>
                </TableHeader>
                <TableBody
                  emptyContent={t('shipments.empty')}
                  items={upcomingArrivals}
                >
                  {(shipment) => (
                    <TableRow key={shipment.id}>
                      <TableCell>
                        {formatDate(shipment.ship_eta, locale)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className='text-sm'>{shipment.exporter_name}</p>
                          <p className='text-xs text-default-500'>
                            {shipment.client_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{shipment.reference_number || '-'}</TableCell>
                      <TableCell>
                        {formatNumber(shipment.total_kilos, locale)}
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          tone={
                            shipment.status === 'settled'
                              ? 'success'
                              : 'warning'
                          }
                        >
                          {shipment.status === 'settled'
                            ? t('shared.settled')
                            : t('shared.pending')}
                        </StatusChip>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardBody>
          </Card>
        </div>
      ) : (
        <Table aria-label={t('shipments.ariaLabel')}>
          <TableHeader>
            <TableColumn>{t('shared.exporter')}</TableColumn>
            <TableColumn>{t('shared.client')}</TableColumn>
            <TableColumn>{t('shared.reference')}</TableColumn>
            <TableColumn>{t('shared.container')}</TableColumn>
            <TableColumn>{t('shared.ship')}</TableColumn>
            <TableColumn>{t('shared.eta')}</TableColumn>
            <TableColumn>{t('shared.boxes')}</TableColumn>
            <TableColumn>{t('shared.kg')}</TableColumn>
            <TableColumn>{t('shared.status')}</TableColumn>
          </TableHeader>
          <TableBody
            emptyContent={t('shipments.empty')}
            items={groupedShipments}
          >
            {(shipment) => (
              <TableRow key={shipment.id}>
                <TableCell>{shipment.exporter_name}</TableCell>
                <TableCell>{shipment.client_name}</TableCell>
                <TableCell>{shipment.reference_number || '-'}</TableCell>
                <TableCell>{shipment.container_number || '-'}</TableCell>
                <TableCell>{shipment.ship_name || '-'}</TableCell>
                <TableCell>{formatDate(shipment.ship_eta, locale)}</TableCell>
                <TableCell>
                  {formatNumber(shipment.total_quantity, locale)}
                </TableCell>
                <TableCell>{formatNumber(shipment.total_kilos, locale)}</TableCell>
                <TableCell>
                  <StatusChip
                    tone={
                      shipment.status === 'settled' ? 'success' : 'warning'
                    }
                  >
                    {shipment.status === 'settled'
                      ? t('shared.settled')
                      : t('shared.pending')}
                  </StatusChip>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </PageLayout>
  );
};

export default ReceiverShipmentsPage;
